import {addDays} from 'date-fns';
import {Box, getSize, Grid, GridItem, Show, useResponsivePropValue} from 'platform/foundation';

import {isPositive} from 'ramda-adjunct';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DAY_COLUMN_TEMPLATE} from '../constants/dayColumnTemplate';
import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import {EVENT_ROW_GAP} from '../constants/eventRowGap';
import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import type {EventSegment} from '../types/EventSegment';
import {layoutEventSegments} from '../utils/layoutEventSegments';
import {MonthCell} from './MonthCell';
import {MonthEventItem} from './MonthEventItem';
import {MonthMoreEvents} from './MonthMoreEvents';

interface MonthWeekRowProps extends RequiredTestIdProps {
  weekStart: Date;
  currentMonth: Date;
  events: BigCalendarEvent[];
  onEventClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

const coversColumn = (segment: EventSegment, column: number) =>
  segment.columnStart <= column && column < segment.columnStart + segment.columnSpan;

/**
 * One week of the month grid. Day cells and an event overlay share the same grid row, so the row
 * grows to fit whichever is taller. A segment is one element spanning all of its day columns,
 * placed on the row the packer assigned, so a multi-day event reads as one continuous bar.
 *
 * Every child is pinned to its grid area explicitly. Auto-placed cells would be pushed past the
 * explicitly placed overlay into implicit columns.
 */
export function MonthWeekRow(props: MonthWeekRowProps) {
  const dayColumns = useResponsivePropValue(DAY_COLUMN_TEMPLATE);
  const days = Array.from({length: DAYS_IN_WEEK}, (_, dayIndex) =>
    addDays(props.weekStart, dayIndex)
  );

  const segments = layoutEventSegments(props.events, props.weekStart);
  const visibleSegments = segments.filter((segment) => segment.slot < MAX_VISIBLE_SLOTS);
  const hiddenSegments = segments.filter((segment) => segment.slot >= MAX_VISIBLE_SLOTS);

  return (
    <Grid columns={DAY_COLUMN_TEMPLATE} spacing={0}>
      {days.map((date, dayIndex) => (
        <GridItem key={date.toISOString()} columnStart={dayIndex + 1} rowStart={1}>
          <Box
            height="100%"
            borderRight={dayIndex === DAYS_IN_WEEK - 1 ? '1px solid' : undefined}
            borderColor="general.separator"
          >
            <MonthCell
              data-testid={suffixTestId(`[${dayIndex}]`, props)}
              date={date}
              currentMonth={props.currentMonth}
            />
          </Box>
        </GridItem>
      ))}

      {/* Hand-written: the overlay must let clicks through to the day cells (a GridItem wrapper
          would catch them) and its 2px row gap is below one design-system step. */}
      <div
        className="pointer-events-none grid min-w-0"
        style={{
          gridColumn: '1 / -1',
          gridRow: 1,
          gridTemplateColumns: dayColumns.template,
          gridAutoRows: getSize(SLOT_HEIGHT),
          rowGap: EVENT_ROW_GAP,
          padding: `${getSize(DAY_NUMBER_HEIGHT)} 0 ${getSize(1)}`,
        }}
      >
        {visibleSegments.map((segment, segmentIndex) => (
          <div
            key={segment.event.id}
            className="pointer-events-auto min-w-0"
            style={{
              gridColumn: `${segment.columnStart + 1} / span ${segment.columnSpan}`,
              gridRow: segment.slot + 1,
              paddingLeft: segment.isStartClipped ? 0 : getSize(1),
              paddingRight: segment.isEndClipped ? 0 : getSize(1),
            }}
          >
            <MonthEventItem
              data-testid={suffixTestId(`event-[${segmentIndex}]`, props)}
              event={segment.event}
              isStartClipped={segment.isStartClipped}
              isEndClipped={segment.isEndClipped}
              onClick={props.onEventClick}
              enableHover={props.enableHover}
              onMouseEnter={props.onEventMouseEnter}
              onMouseLeave={props.onEventMouseLeave}
            />
          </div>
        ))}

        {days.map((date, dayIndex) => {
          const hiddenEvents = hiddenSegments
            .filter((segment) => coversColumn(segment, dayIndex))
            .map((segment) => segment.event);

          return (
            <Show key={date.toISOString()} when={isPositive(hiddenEvents.length)}>
              <div
                className="pointer-events-auto min-w-0"
                style={{
                  gridColumn: dayIndex + 1,
                  gridRow: MAX_VISIBLE_SLOTS + 1,
                  padding: `0 ${getSize(1)}`,
                }}
              >
                <MonthMoreEvents
                  data-testid={suffixTestId(`[${dayIndex}]`, props)}
                  date={date}
                  hiddenEvents={hiddenEvents}
                  onEventClick={props.onEventClick}
                  enableHover={props.enableHover}
                  onEventMouseEnter={props.onEventMouseEnter}
                  onEventMouseLeave={props.onEventMouseLeave}
                />
              </div>
            </Show>
          );
        })}
      </div>
    </Grid>
  );
}

/** Rows of events drawn per week before the rest collapses into "+N more". */
const MAX_VISIBLE_SLOTS = 3;
/** Height of one event row, matching the event item and the "+N more" chip. */
const SLOT_HEIGHT = 5;
/** Space the overlay leaves for the day number drawn by the cell underneath. */
const DAY_NUMBER_HEIGHT = 7;
